import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Task-editing cluster for the project detail view: a task row with its
// progress pill, due-date + recurrence editors, checklist rows + add form, the
// meeting-cadence summary, and the schedule-meeting modal.
//
// Extracted from the oversized pages/Projects.tsx (P2 compartmentalization).
// DescriptionEditor moves here too because TaskRow renders it; it's re-exported
// for ProjectDetail (which stays in Projects.tsx and imports it back, a clean
// one-way dependency). Only TaskRow + DescriptionEditor are exported; the rest
// are module-internal. Pure move — behavior unchanged.
import { notify } from "../../components/Toast";
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import MultiSelect from "../../components/MultiSelect";
import ProgressBar from "../../components/ProgressBar";
import TaskCompletionModal from "../../components/TaskCompletionModal";
import TimeSelect from "../../components/TimeSelect";
export function DescriptionEditor({ value, onSave, canEdit, placeholder, }) {
    const [draft, setDraft] = useState(value ?? "");
    // Keep local draft in sync if the upstream value changes (e.g., after
    // another admin updates it). We compare to draft to avoid clobbering
    // mid-typing edits.
    useEffect(() => {
        setDraft(value ?? "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    async function flush() {
        const trimmed = draft.trim();
        const current = (value ?? "").trim();
        if (trimmed === current)
            return;
        await onSave(trimmed ? trimmed : null);
    }
    if (!canEdit) {
        if (!value || !value.trim())
            return null;
        return _jsx("p", { className: "muted description-readonly", children: value });
    }
    return (_jsxs("label", { className: "description-editor", children: [_jsx("span", { className: "add-named-label", children: "Description" }), _jsx("textarea", { rows: 3, value: draft, placeholder: placeholder, onChange: (e) => setDraft(e.target.value), onBlur: flush })] }));
}
// Compact "Xh Ym" formatter — mirrors ProjectDetail.formatMinutes. Kept
// local to avoid a circular import (ProjectDetail imports TaskRow).
function fmtMinutes(total) {
    const m = Math.max(0, Math.round(total));
    if (m === 0)
        return "0m";
    const h = Math.floor(m / 60);
    const mm = m % 60;
    if (h === 0)
        return `${mm}m`;
    if (mm === 0)
        return `${h}h`;
    return `${h}h ${mm}m`;
}
// Time pill on every task card. Read-only for workers; admin / manager /
// owner double-click to switch to an inline hours + minutes editor that
// PATCHes `actual_minutes_override` on the task — works on completed
// tasks too (the point of the override is fixing finished work). The
// orchid dot signals an admin has corrected this value previously.
//
// Sending `-1` clears the override and falls back to the live derived
// number (block sum + extra_minutes_logged); the "Reset" button does that.
function TaskTimeEditor({ task, canEdit, onChanged, }) {
    const current = task.minutes_spent ?? 0;
    const [editing, setEditing] = useState(false);
    const [hours, setHours] = useState(() => Math.floor(current / 60));
    const [mins, setMins] = useState(() => current % 60);
    useEffect(() => {
        if (!editing) {
            setHours(Math.floor((task.minutes_spent ?? 0) / 60));
            setMins((task.minutes_spent ?? 0) % 60);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task.minutes_spent]);
    async function save() {
        const next = Math.max(0, Math.round(hours * 60 + mins));
        if (next === current) {
            setEditing(false);
            return;
        }
        try {
            await api.patch(`/tasks/${task.id}`, { actual_minutes_override: next });
            setEditing(false);
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't update time spent");
            setEditing(false);
        }
    }
    async function reset() {
        try {
            // -1 is the explicit "clear the override" sentinel — the column is
            // nullable, but Pydantic with default=None can't distinguish "field
            // not sent" from "set to null", so we route the clear through -1.
            await api.patch(`/tasks/${task.id}`, { actual_minutes_override: -1 });
            setEditing(false);
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't reset time");
            setEditing(false);
        }
    }
    if (!editing) {
        return (_jsxs("span", { className: `task-time-pill ${task.is_time_overridden ? "is-overridden" : ""}`, title: canEdit
                ? task.is_time_overridden
                    ? "Adjusted by an admin — double-click to edit again, or use Reset in the editor to fall back to the live block sum."
                    : "Total time spent on this task — double-click to adjust."
                : task.is_time_overridden
                    ? "Total time spent (adjusted by an admin)."
                    : "Total time spent on this task.", onDoubleClick: (e) => {
                if (!canEdit)
                    return;
                e.stopPropagation();
                setEditing(true);
            }, children: [task.is_time_overridden && (_jsx("span", { className: "task-time-pill-adj", "aria-hidden": "true" })), _jsx("span", { className: "task-time-pill-icon", "aria-hidden": "true", children: "\u23F1" }), fmtMinutes(current)] }));
    }
    return (_jsxs("span", { className: "task-time-pill task-time-pill-editing", onClick: (e) => e.stopPropagation(), onDoubleClick: (e) => e.stopPropagation(), children: [_jsx("input", { type: "number", min: 0, max: 1000, value: hours, onChange: (e) => setHours(Math.max(0, parseInt(e.target.value || "0", 10))), onKeyDown: (e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        save();
                    }
                    else if (e.key === "Escape") {
                        e.preventDefault();
                        setEditing(false);
                    }
                }, autoFocus: true, "aria-label": "Hours" }), _jsx("span", { className: "task-time-pill-sep", children: "h" }), _jsx("input", { type: "number", min: 0, max: 59, step: 15, value: mins, onChange: (e) => setMins(Math.min(59, Math.max(0, parseInt(e.target.value || "0", 10)))), onKeyDown: (e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        save();
                    }
                    else if (e.key === "Escape") {
                        e.preventDefault();
                        setEditing(false);
                    }
                }, "aria-label": "Minutes" }), _jsx("span", { className: "task-time-pill-sep", children: "m" }), _jsx("button", { type: "button", className: "task-time-pill-btn", onClick: save, title: "Save", children: "\u2713" }), task.is_time_overridden && (_jsx("button", { type: "button", className: "task-time-pill-btn task-time-pill-reset", onClick: reset, title: "Reset to live block-derived total", children: "\u21BA" })), _jsx("button", { type: "button", className: "task-time-pill-btn task-time-pill-cancel", onClick: () => setEditing(false), title: "Cancel", children: "\u2715" })] }));
}
// Inline title editor — renders as a plain heading until the user
// double-clicks (admin/manager/owner only). Save on blur or Enter, cancel
// on Escape. Clicks/dblclicks stop propagation so the card's expand-toggle
// doesn't fire while you're renaming. Empty / unchanged titles are no-ops.
function TaskTitleEditor({ task, canEdit, onChanged, }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(task.title);
    // Keep the local draft in sync if the upstream title changes (e.g. someone
    // else renamed it). Compare to current draft to avoid clobbering mid-edit.
    useEffect(() => {
        if (!editing)
            setDraft(task.title);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task.title]);
    async function save() {
        const next = draft.trim();
        if (!next || next === task.title) {
            setDraft(task.title);
            setEditing(false);
            return;
        }
        try {
            await api.patch(`/tasks/${task.id}`, { title: next });
            setEditing(false);
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't rename task");
            setDraft(task.title);
            setEditing(false);
        }
    }
    if (!editing) {
        return (_jsx("h3", { className: "task-title", onDoubleClick: (e) => {
                if (!canEdit)
                    return;
                e.stopPropagation();
                setEditing(true);
            }, title: canEdit ? "Double-click to rename" : undefined, children: task.title }));
    }
    return (_jsx("input", { className: "task-title task-title-input", autoFocus: true, value: draft, onChange: (e) => setDraft(e.target.value), onBlur: save, onClick: (e) => e.stopPropagation(), onDoubleClick: (e) => e.stopPropagation(), onKeyDown: (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                e.target.blur();
            }
            else if (e.key === "Escape") {
                e.preventDefault();
                setDraft(task.title);
                setEditing(false);
            }
        } }));
}
function MeetingCadenceSummary({ task }) {
    if (!task.recurrence_frequency) {
        return (_jsx("div", { className: "muted small meeting-cadence-empty", children: "No schedule set yet \u2014 the event creator hasn't picked a time." }));
    }
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    // HH:MM:SS → "9:00 AM" (locale-aware AM/PM)
    function fmtTime(hms) {
        const [h, m] = hms.split(":").map(Number);
        const d = new Date();
        d.setHours(h || 0, m || 0, 0, 0);
        return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    }
    const time = task.recurrence_time_of_day
        ? fmtTime(task.recurrence_time_of_day)
        : "—";
    const duration = task.recurrence_duration_minutes ?? 30;
    let when;
    if (task.recurrence_frequency === "daily") {
        when = `Every day at ${time}`;
    }
    else if (task.recurrence_frequency === "weekly") {
        const days = (task.recurrence_days_of_week ?? [])
            .map((i) => dayNames[i])
            .join(", ");
        when = `${days || "(no days picked)"} at ${time}`;
    }
    else {
        when = `Monthly on day ${task.recurrence_day_of_month ?? 1} at ${time}`;
    }
    return (_jsxs("div", { className: "meeting-cadence-summary", children: [_jsx("strong", { children: when }), _jsxs("span", { className: "muted small", children: ["\u00B7 ", duration === 60 ? "1 hour" : `${duration} min`, " \u00B7 Eastern Time"] })] }));
}
// Compact, read-only progress pill rendered in the task header. Progress
// is now fully derived from the checklist (backend recomputes on every
// item create / update / delete), so this widget is purely a display —
// no slider, no PATCH. Shows "X / Y done · Z %" plus a slim bar so the
// user can read both shapes at a glance.
function TaskProgressPill({ task }) {
    const items = task.checklist_items ?? [];
    const total = items.length;
    const done = items.filter((c) => c.is_completed).length;
    // No checklist → progress comes from whatever the task itself was set
    // to (e.g. via the time-block "Complete?" flow). Fall back to that.
    const pct = total > 0 ? Math.round((done / total) * 100) : task.progress_percent;
    return (_jsxs("span", { className: "task-progress-pill", title: total > 0
            ? `Progress is calculated from this task's checklist — ${done} of ${total} items done.`
            : "No checklist on this task. Add items to track progress here.", children: [total > 0 ? (_jsxs("span", { className: "task-progress-pill-count", children: [done, "/", total, " done"] })) : (_jsx("span", { className: "task-progress-pill-count muted", children: "No checklist" })), _jsx("span", { className: "task-progress-pill-bar", children: _jsx(ProgressBar, { value: pct, size: "small", showLabel: false }) }), _jsxs("span", { className: "task-progress-pill-pct", children: [pct, "%"] })] }));
}
// "Schedule as meeting" modal — turns a task into a scheduled meeting.
// Picks attendees + a time and posts to /tasks/{id}/schedule-meeting,
// which drops a block on each attendee's calendar linked to the task (so
// the time credits the project and everyone on the call is credited).
function ScheduleMeetingModal({ taskId, taskTitle, members, currentUserId, onClose, onScheduled, }) {
    const [attendees, setAttendees] = useState([]);
    const [includeMe, setIncludeMe] = useState(true);
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [time, setTime] = useState("09:00");
    const [duration, setDuration] = useState(30);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    async function confirm() {
        const ids = new Set(attendees);
        if (includeMe && currentUserId)
            ids.add(currentUserId);
        if (ids.size === 0) {
            setError("Add at least one attendee (or include yourself).");
            return;
        }
        // Build a local-time Date from the date + time inputs, then send UTC.
        const start = new Date(`${date}T${time}:00`);
        if (Number.isNaN(start.getTime())) {
            setError("Pick a valid date and time.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await api.post(`/tasks/${taskId}/schedule-meeting`, {
                attendee_ids: Array.from(ids),
                start_at: start.toISOString(),
                duration_minutes: duration,
            });
            await onScheduled();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't schedule the meeting");
            setSaving(false);
        }
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "Schedule as meeting" }), _jsx("p", { className: "muted small", style: { marginTop: "-0.5rem" }, children: taskTitle }), _jsxs("label", { children: ["Attendees", _jsx(MultiSelect, { options: members.map((m) => ({ id: m.id, label: m.name })), values: attendees, onChange: setAttendees, placeholder: "Add attendees", renderTrigger: (sel) => (_jsx("span", { className: "pill assignees-pill", children: sel.length === 0 ? "Pick attendees" : `${sel.length} invited` })) })] }), _jsxs("label", { className: "inline-checkbox", children: [_jsx("input", { type: "checkbox", checked: includeMe, onChange: (e) => setIncludeMe(e.target.checked) }), "Include me"] }), _jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Date", _jsx("input", { type: "date", value: date, onChange: (e) => setDate(e.target.value) })] }), _jsxs("label", { children: ["Start time", _jsx("input", { type: "time", value: time, onChange: (e) => setTime(e.target.value) })] }), _jsxs("label", { children: ["Duration", _jsxs("select", { value: duration, onChange: (e) => setDuration(Number(e.target.value)), children: [_jsx("option", { value: 15, children: "15 min" }), _jsx("option", { value: 30, children: "30 min" }), _jsx("option", { value: 45, children: "45 min" }), _jsx("option", { value: 60, children: "1 hour" }), _jsx("option", { value: 90, children: "1.5 hours" }), _jsx("option", { value: 120, children: "2 hours" })] })] })] }), _jsx("p", { className: "muted small", children: "Everyone invited gets this on their calendar, linked to this task \u2014 so the time credits the project and each attendee. Anyone with a conflict gets an Accept/Decline request in Awaiting Review." }), error && _jsx("div", { className: "error", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", onClick: onClose, disabled: saving, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", onClick: confirm, disabled: saving, children: saving ? "Scheduling…" : "Schedule meeting" })] })] }) }));
}
export function TaskRow({ task, allTasks, members, isAdmin, currentUserId, projectType, projectId, projectOwnerId, depth, onChanged, flowPosition, flowTotal, isWaiting = false, predecessorTitles, }) {
    // When the user clicks "Mark complete", we route through the completion
    // modal (which captures time spent + BU/Activity + contributors) instead
    // of silently flipping status — so completion always logs how long it took.
    const [completing, setCompleting] = useState(false);
    // "Schedule as meeting" — turn this task into a scheduled meeting with
    // attendees (e.g. a Discovery Call), dropping a block on each attendee's
    // calendar linked to the task so everyone is credited.
    const [scheduling, setScheduling] = useState(false);
    // Collapsible card. Collapsed (default) shows just the header — title, key
    // info (progress, due, assignees) and quick actions (Mark complete /
    // Schedule). Expanding reveals the task notes, checklist, sub-tasks, and
    // the Delete action so the list stays scannable until you dig in.
    const [expanded, setExpanded] = useState(false);
    const isRecurringMeeting = projectType === "recurring_event";
    // Only the project owner — or a site admin acting as safety override — can
    // edit recurrence fields on a meeting task. The currentUserId comes from
    // useAuth() in the parent.
    const canEditRecurrence = currentUserId === projectOwnerId || isAdmin;
    const children = allTasks.filter((t) => t.parent_task_id === task.id);
    const isSelfAssigned = currentUserId != null && task.assignee_ids.includes(currentUserId);
    // Names for the assignee-pill hover tooltip. `members` is the
    // project's roster so we can resolve each id → name; if a member
    // isn't in the list (left the org, lost BU access), we fall back to
    // a short id stub so the tooltip never silently drops a person.
    const assigneeTooltip = task.assignee_ids.length === 0
        ? ""
        : task.assignee_ids
            .map((id) => {
            const m = members.find((u) => u.id === id);
            return m?.name ?? `(${id.slice(0, 6)}…)`;
        })
            .join("\n");
    // (was: per-checkbox toggleAssignee — replaced by the MultiSelect dropdown's
    // diff-on-change flow below. Left as a code comment so it's clear why the
    // old <details> wiring is gone.)
    async function selfClaim() {
        // Self-claim is idempotent server-side; the button is only shown when
        // currentUserId isn't already in the assignee set, but be defensive.
        await api.post(`/tasks/${task.id}/claim`);
        onChanged();
    }
    async function selfUnclaim() {
        if (!currentUserId)
            return;
        await api.del(`/tasks/${task.id}/assignees/${currentUserId}`);
        onChanged();
    }
    async function deleteTask() {
        // Pre-flight: pull the schedule-impact summary so the confirm dialog
        // can tell the user exactly whose calendars will lose entries. Falls
        // through to a generic warning if the impact endpoint errors so the
        // admin can still delete a task even when the count is unavailable.
        let warning = "";
        try {
            const impact = await api.get(`/tasks/${task.id}/schedule-impact`);
            if (impact.block_count > 0) {
                const namesLine = impact.user_names.length > 0
                    ? `\n\nOn ${impact.user_count === 1 ? "this person's" : "these people's"} calendar: ${impact.user_names.join(", ")}${impact.user_count > impact.user_names.length
                        ? ` +${impact.user_count - impact.user_names.length} more`
                        : ""}`
                    : "";
                warning =
                    `\n\n⚠ This task is on someone's time block schedule.\n` +
                        `${impact.block_count} block${impact.block_count === 1 ? "" : "s"} across ` +
                        `${impact.user_count} user${impact.user_count === 1 ? "" : "s"} will be removed.${namesLine}`;
            }
        }
        catch {
            /* fall through to generic confirm — better to let the admin delete
               than to block when the preview endpoint is unavailable. */
        }
        if (!confirm(`Delete "${task.title}"? This permanently removes the task, all its checklist items, ` +
            `and any recurring patterns sourced from it. This cannot be undone.${warning}`)) {
            return;
        }
        try {
            await api.del(`/tasks/${task.id}`);
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't delete task");
        }
    }
    // Visual treatment for finished tasks: dim the card, strike the
    // title, and show a green "✓ Done" badge so it reads as completed at
    // a glance instead of hiding inside the progress pill.
    const isTaskDone = task.status === "completed";
    return (_jsxs("article", { className: `task-card ${isRecurringMeeting ? "task-card-meeting" : ""} ${depth > 0 ? "task-card-sub" : ""} ${isTaskDone ? "task-card-done" : ""} ${isWaiting ? "task-card-waiting" : ""}`, style: depth > 0 ? { marginLeft: depth * 16 } : undefined, children: [_jsxs("div", { className: "task-head task-head-clickable", role: "button", tabIndex: 0, "aria-expanded": expanded, title: expanded
                    ? "Click to collapse"
                    : "Click to expand — notes, checklist & more", onClick: (e) => {
                    // Click the header to expand/collapse — but ignore clicks on the
                    // interactive controls (assignee picker, due-date input, action
                    // buttons) so they keep doing their own thing.
                    const el = e.target;
                    if (el.closest('button, input, select, textarea, a, label, .assignees-pill, [role="button"]'))
                        return;
                    setExpanded((v) => !v);
                }, onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        const el = e.target;
                        if (el.closest('button, input, select, textarea, a, label, .assignees-pill'))
                            return;
                        e.preventDefault();
                        setExpanded((v) => !v);
                    }
                }, children: [_jsx("span", { className: "task-caret", "aria-hidden": "true", children: expanded ? "▾" : "▸" }), flowPosition != null && flowTotal != null && (_jsxs("span", { className: "task-step-badge", title: "Position in the project's task order", children: [flowPosition, " of ", flowTotal] })), _jsx(TaskTitleEditor, { task: task, canEdit: isAdmin, onChanged: onChanged }), _jsxs("div", { className: "task-head-meta", children: [isTaskDone && (_jsx("span", { className: "pill ok task-done-pill", title: "This task is marked complete", children: "\u2713 Done" })), !isRecurringMeeting && _jsx(TaskProgressPill, { task: task }), !isRecurringMeeting && (_jsx(TaskTimeEditor, { task: task, canEdit: isAdmin, onChanged: onChanged })), !isRecurringMeeting &&
                                !isTaskDone &&
                                (task.checklist_items?.length ?? 0) === 0 &&
                                (isAdmin || isSelfAssigned) && (_jsx("button", { type: "button", className: "btn-primary task-complete-btn", onClick: () => setCompleting(true), title: "Mark this task complete \u2014 logs time spent + credits contributors", children: "Mark complete" })), !isRecurringMeeting && !isTaskDone && isAdmin && (_jsx("button", { type: "button", className: "btn-link task-meeting-btn", onClick: () => setScheduling(true), title: "Schedule this task as a meeting with attendees", children: "\uD83D\uDCC5 Schedule as meeting" })), completing && (_jsx(TaskCompletionModal, { taskId: task.id, projectId: projectId, taskTitle: task.title, onClose: () => setCompleting(false), onCompleted: async () => {
                                    setCompleting(false);
                                    onChanged();
                                } })), scheduling && (_jsx(ScheduleMeetingModal, { taskId: task.id, taskTitle: task.title, members: members, currentUserId: currentUserId, onClose: () => setScheduling(false), onScheduled: async () => {
                                    setScheduling(false);
                                    onChanged();
                                } })), !isRecurringMeeting && (_jsx(TaskDueDate, { task: task, isAdmin: isAdmin, onChanged: onChanged })), isAdmin ? (
                            // See who's on it + assign / unassign in one click — always
                            // visible so the card surfaces who's working on the task.
                            _jsx(MultiSelect, { options: members.map((m) => ({ id: m.id, label: m.name })), values: task.assignee_ids, onChange: async (next) => {
                                    // Diff the new set against the old and fire the right
                                    // add/remove endpoint per change. We do this on the
                                    // controlled-value emit so the server stays in sync per click.
                                    const old = task.assignee_ids;
                                    const added = next.filter((id) => !old.includes(id));
                                    const removed = old.filter((id) => !next.includes(id));
                                    for (const id of added) {
                                        await api.post(`/tasks/${task.id}/assignees`, { user_id: id });
                                    }
                                    for (const id of removed) {
                                        await api.del(`/tasks/${task.id}/assignees/${id}`);
                                    }
                                    // For recurring meetings, also push the cadence onto each
                                    // newly-added assignee's calendar automatically. The endpoint
                                    // is idempotent — it'll refresh existing patterns and create
                                    // new ones — so calling it once after the diff covers all
                                    // additions in this single multi-select edit.
                                    if (isRecurringMeeting && added.length > 0 && task.recurrence_frequency) {
                                        try {
                                            await api.post(`/tasks/${task.id}/schedule-for-assignees`);
                                        }
                                        catch {
                                            // Schedule push is best-effort here — the assignment itself
                                            // succeeded. The admin can also click "Save & push" on the
                                            // recurrence editor if this silently fails.
                                        }
                                    }
                                    onChanged();
                                }, placeholder: "Add / remove people", renderTrigger: (sel) => (_jsx("span", { className: "pill assignees-pill", title: assigneeTooltip || undefined, children: sel.length === 0 ? "Unassigned" : `${sel.length} assigned` })) })) : (
                            // Worker view: see who's on it + a one-tap self-claim/release.
                            currentUserId && (_jsxs("span", { className: "task-due", children: [_jsx("span", { className: "pill assignees-pill", title: assigneeTooltip || undefined, children: task.assignee_ids.length === 0
                                            ? "Unassigned"
                                            : `${task.assignee_ids.length} on it` }), isSelfAssigned ? (_jsx("button", { className: "btn-link", onClick: selfUnclaim, children: "Drop" })) : (_jsx("button", { className: "btn-link", onClick: selfClaim, children: "Claim" }))] }))), isAdmin && (_jsx("button", { type: "button", className: "task-edit-fab", onClick: () => setExpanded((v) => !v), title: "Edit this task \u2014 notes, checklist & more", children: "\u270E Edit" }))] })] }), isWaiting && predecessorTitles && predecessorTitles.length > 0 && (_jsxs("div", { className: "task-dep-note", title: "Tasks this one depends on", children: ["\u26D3 after ", predecessorTitles.join(" · ")] })), expanded && (_jsxs(_Fragment, { children: [_jsx(DescriptionEditor, { value: task.description, canEdit: isAdmin, placeholder: "Task notes \u2014 context, success criteria, links, anything users need to know.", onSave: async (next) => {
                            await api.patch(`/tasks/${task.id}`, { description: next });
                            onChanged();
                        } }), isRecurringMeeting && (_jsxs("div", { className: "meeting-recurrence-block", children: [_jsxs("div", { className: "meeting-recurrence-head", children: [_jsx("span", { className: "recurring-pill pill", children: "\u21BB Meeting recurrence" }), _jsx("span", { className: "muted small", children: canEditRecurrence
                                            ? "Set when this meeting repeats — daily, weekly, or monthly."
                                            : "Only the event creator can change the date or time." })] }), _jsx(MeetingCadenceSummary, { task: task }), canEditRecurrence && (_jsx(TaskRecurrenceEditor, { task: task, onChanged: onChanged }))] })), (!isRecurringMeeting || (task.checklist_items?.length ?? 0) > 0) && (_jsxs("div", { className: "checklist-section", children: [(isAdmin || (task.checklist_items?.length ?? 0) > 0) && (_jsx("div", { className: "checklist-section-head", children: _jsx("strong", { children: "Checklist" }) })), task.checklist_items && task.checklist_items.length > 0 && (_jsx("ul", { className: "checklist", children: task.checklist_items.map((c) => (_jsx(ChecklistRow, { item: c, members: members, isAdmin: isAdmin, onChanged: onChanged }, c.id))) })), isAdmin && (_jsx(NewChecklistItemForm, { taskId: task.id, members: members, onChanged: onChanged }))] })), children.map((child) => (_jsx(TaskRow, { task: child, allTasks: allTasks, members: members, isAdmin: isAdmin, currentUserId: currentUserId, projectType: projectType, projectId: projectId, projectOwnerId: projectOwnerId, depth: depth + 1, onChanged: onChanged }, child.id))), isAdmin && (_jsx("div", { className: "task-edit-footer", children: _jsx("button", { type: "button", className: "btn-link task-delete-btn", onClick: deleteTask, title: "Permanently delete this task (and any scheduled blocks linked to it)", children: "Delete task" }) }))] }))] }));
}
function TaskDueDate({ task, isAdmin, onChanged, }) {
    const [value, setValue] = useState(task.due_date ?? "");
    async function save() {
        if (value === (task.due_date ?? ""))
            return;
        await api.patch(`/tasks/${task.id}`, { due_date: value || null });
        onChanged();
    }
    if (!isAdmin) {
        return task.due_date ? (_jsxs("span", { className: "task-due muted", children: ["due ", task.due_date] })) : null;
    }
    return (_jsxs("span", { className: "task-due", children: [_jsx("span", { className: "muted", children: "due" }), _jsx("input", { type: "date", value: value, onChange: (e) => setValue(e.target.value), onBlur: save })] }));
}
function TaskRecurrenceEditor({ task, onChanged, }) {
    const [open, setOpen] = useState(false);
    const [frequency, setFrequency] = useState(task.recurrence_frequency ?? "weekly");
    const [daysOfWeek, setDaysOfWeek] = useState(task.recurrence_days_of_week ?? [0, 1, 2, 3, 4]);
    const [dayOfMonth, setDayOfMonth] = useState(task.recurrence_day_of_month ?? 1);
    const [timeOfDay, setTimeOfDay] = useState(task.recurrence_time_of_day ? task.recurrence_time_of_day.slice(0, 5) : "09:00");
    const [duration, setDuration] = useState(task.recurrence_duration_minutes === 60 ? 60 : 30);
    // Timezone is locked to Eastern across the app — no setter. Persisted with
    // each PATCH so the server's stored value stays consistent (it would force
    // it server-side anyway, but keeping the round-trip clean).
    const tz = "America/New_York";
    const [busy, setBusy] = useState(false);
    const hasRecurrence = task.recurrence_frequency != null;
    function toggleDay(d) {
        setDaysOfWeek((cur) => cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort());
    }
    // Centralized PATCH so save() and saveAndPush() share the same payload.
    async function patchRecurrence() {
        await api.patch(`/tasks/${task.id}`, {
            recurrence_frequency: frequency,
            recurrence_days_of_week: frequency === "weekly" ? daysOfWeek : null,
            recurrence_day_of_month: frequency === "monthly" ? dayOfMonth : null,
            recurrence_time_of_day: `${timeOfDay}:00`,
            recurrence_duration_minutes: duration,
            recurrence_timezone: tz || null,
        });
    }
    async function save() {
        setBusy(true);
        try {
            await patchRecurrence();
            onChanged();
            setOpen(false);
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Save failed");
        }
        finally {
            setBusy(false);
        }
    }
    // Save the recurrence AND push it onto every current assignee's calendar.
    // Calls /tasks/{id}/schedule-for-assignees after the PATCH, which creates
    // (or refreshes) a RecurringPattern per assignee and materializes blocks.
    // Surfaces the server's count message so the admin sees how many users
    // were affected.
    async function saveAndPush() {
        setBusy(true);
        try {
            await patchRecurrence();
            const r = await api.post(`/tasks/${task.id}/schedule-for-assignees`);
            onChanged();
            setOpen(false);
            const total = r.created + r.updated;
            // Three outcomes the user needs to distinguish:
            //   1) No assignees at all → tell them to assign someone first.
            //   2) Patterns updated but 0 blocks materialized → the schedule is set
            //      but the materializer didn't produce any future occurrences (most
            //      common cause: start_date is far in the future, or end_date has
            //      already passed). Surface this clearly so it doesn't look like
            //      a generic success that "just didn't work".
            //   3) Real success — say how many calendars and blocks it touched.
            if (total === 0) {
                // Two assignment levels exist — task-level (the multi-select on the
                // meeting block) and project-level (the "+ Add / remove people" at
                // the top of the project). The endpoint checks task first, then
                // falls back to project. If both are empty we reach this branch.
                notify.error("Saved the recurrence — but no one is assigned at the task OR " +
                    "project level yet, so nothing was scheduled. Add someone with " +
                    "the '+ Add / remove people' picker (either on this meeting or " +
                    "at the top of the project), then re-run.");
            }
            else if (r.blocks === 0) {
                notify.error(`Scheduled the recurrence on ${total} assignee calendar` +
                    `${total === 1 ? "" : "s"}, but no time blocks were created in the ` +
                    "current materialization window. Double-check the frequency/day " +
                    "selection — for example, 'Weekly' with no days picked produces no " +
                    "occurrences.");
            }
            else {
                notify.error(`Scheduled on ${total} assignee calendar${total === 1 ? "" : "s"} ` +
                    `(${r.created} new, ${r.updated} refreshed, ${r.blocks} blocks ` +
                    "materialized). Have the assignees refresh their calendar to see the blocks.");
            }
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Schedule failed");
        }
        finally {
            setBusy(false);
        }
    }
    async function clearRecurrence() {
        if (!confirm("Clear the meeting's recurrence defaults?"))
            return;
        setBusy(true);
        try {
            await api.patch(`/tasks/${task.id}`, {
                recurrence_frequency: null,
                recurrence_days_of_week: null,
                recurrence_day_of_month: null,
                recurrence_time_of_day: null,
                recurrence_duration_minutes: null,
                recurrence_timezone: null,
            });
            onChanged();
        }
        finally {
            setBusy(false);
        }
    }
    if (!open) {
        return (_jsx("div", { className: "task-recurrence-summary", children: hasRecurrence ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "pill recurring-pill", children: "\u21BB Recurs:" }), _jsxs("span", { className: "muted", children: [frequency === "daily"
                                ? `Daily at ${timeOfDay}`
                                : frequency === "weekly"
                                    ? `${daysOfWeek
                                        .map((d) => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][d])
                                        .join(", ")} at ${timeOfDay}`
                                    : `Day ${dayOfMonth} at ${timeOfDay}`, "\u00B7 ", duration === 60 ? "1 hr" : `${duration} min`, tz && ` · ${tz}`] }), _jsx("button", { className: "btn-link", onClick: () => setOpen(true), children: "Edit" }), _jsx("button", { className: "btn-link", onClick: clearRecurrence, disabled: busy, children: "Clear" })] })) : (_jsx("button", { className: "btn-link", onClick: () => setOpen(true), children: "+ Set meeting recurrence" })) }));
    }
    // Render the editor as a true modal so it matches the calendar's recurring-
    // pattern modal (same shape: backdrop + centered card). Clicking the backdrop
    // dismisses. The trigger button stays inline above so admins can see the
    // current recurrence summary at a glance and pop the editor on demand.
    return (_jsx("div", { className: "modal-backdrop", onClick: () => !busy && setOpen(false), children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "Meeting recurrence" }), _jsx("p", { className: "muted", style: { marginTop: "-0.5rem", fontSize: "0.85rem" }, children: "Choose how often this meeting repeats. Saving with \"push to all assignees\" creates a recurring pattern on every current assignee's calendar." }), _jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Frequency", _jsxs("select", { value: frequency, onChange: (e) => setFrequency(e.target.value), children: [_jsx("option", { value: "daily", children: "Daily" }), _jsx("option", { value: "weekly", children: "Weekly" }), _jsx("option", { value: "monthly", children: "Monthly" })] })] }), _jsxs("label", { children: ["Time", _jsx(TimeSelect, { value: timeOfDay, onChange: setTimeOfDay })] })] }), frequency === "weekly" && (_jsxs("label", { children: ["Days of week", _jsx("div", { className: "dow-row", children: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label, i) => (_jsx("button", { type: "button", className: `dow-btn ${daysOfWeek.includes(i) ? "active" : ""}`, onClick: () => toggleDay(i), children: label }, i))) })] })), frequency === "monthly" && (_jsxs("label", { children: ["Day of month", _jsx("input", { type: "number", min: 1, max: 31, value: dayOfMonth, onChange: (e) => setDayOfMonth(Number(e.target.value)) })] })), _jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Duration", _jsx("select", { value: duration, onChange: (e) => setDuration(Number(e.target.value)), children: [15, 30, 45, 60, 75, 90, 105, 120].map((m) => (_jsx("option", { value: m, children: m % 60 === 0
                                            ? `${m / 60} hour${m === 60 ? "" : "s"}`
                                            : `${m} min` }, m))) })] }), _jsxs("label", { children: ["Timezone", _jsx("input", { value: "Eastern Time (America/New_York)", readOnly: true, disabled: true })] })] }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: () => setOpen(false), disabled: busy, children: "Cancel" }), _jsx("button", { onClick: save, disabled: busy, children: busy ? "Saving…" : "Save defaults only" }), _jsx("button", { onClick: saveAndPush, className: "btn-primary", disabled: busy, children: busy ? "Saving…" : "Save & push to all assignees" })] }), _jsxs("p", { className: "muted small", children: [_jsx("strong", { children: "Defaults only" }), " stores the recurrence on the task \u2014 users still schedule it themselves from the side panel.", _jsx("strong", { children: " Push to all assignees" }), " creates a recurring pattern on every current assignee's calendar right away."] })] }) }));
}
function ChecklistRow({ item, members, isAdmin, onChanged, }) {
    const assignee = members.find((m) => m.id === item.assignee_id);
    async function setCompleted(v) {
        await api.patch(`/tasks/checklist-items/${item.id}`, { is_completed: v });
        onChanged();
    }
    async function setAssignee(userId) {
        await api.patch(`/tasks/checklist-items/${item.id}`, { assignee_id: userId || null });
        onChanged();
    }
    return (_jsxs("li", { className: `checklist-item ${item.is_completed ? "done" : ""}`, 
        // Whole-row click toggles completion — easier target than the 16-px
        // checkbox. Form controls inside the row stop propagation so
        // clicking the select/inputs doesn't flip completion by accident.
        onClick: (e) => {
            const tag = e.target.tagName;
            if (tag === "INPUT" || tag === "SELECT" || tag === "BUTTON")
                return;
            setCompleted(!item.is_completed);
        }, role: "checkbox", "aria-checked": item.is_completed, tabIndex: 0, onKeyDown: (e) => {
            if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                setCompleted(!item.is_completed);
            }
        }, style: { cursor: "pointer" }, children: [_jsx("input", { type: "checkbox", checked: item.is_completed, onChange: (e) => setCompleted(e.target.checked), onClick: (e) => e.stopPropagation() }), _jsx("span", { className: "checklist-title", children: item.title }), isAdmin ? (
            // Compact assignee picker — sized to fit short names without
            // dominating the row. Full picker still available behind the
            // dropdown click.
            _jsxs("select", { className: "checklist-assignee", value: item.assignee_id ?? "", onChange: (e) => setAssignee(e.target.value), title: "Assignee", children: [_jsx("option", { value: "", children: "\u2014 unassigned \u2014" }), members.map((m) => (_jsx("option", { value: m.id, children: m.name }, m.id)))] })) : (_jsx("span", { className: "muted small", children: assignee?.name ?? "unassigned" }))] }));
}
// NewTaskForm removed — task creation now happens via the inline
// ProjectAddTaskInline component on the project detail page, which
// captures the title, due date, assignees, and checklist items in one
// expanding form. Sub-task nesting is no longer surfaced as a separate
// inline form per row; if we bring it back, do it as a row-level
// "+ Sub-task" button that reuses the same form pattern.
function NewChecklistItemForm({ taskId, members, onChanged, }) {
    const [title, setTitle] = useState("");
    const [assigneeId, setAssigneeId] = useState("");
    const [error, setError] = useState(null);
    // Compact single-row form: a slim text input + assignee.
    // Submits on Enter so the user never has to hunt for a save button.
    async function submit() {
        if (!title.trim())
            return;
        setError(null);
        try {
            await api.post("/tasks/checklist-items", {
                task_id: taskId,
                title: title.trim(),
                assignee_id: assigneeId || null,
            });
            setTitle("");
            setAssigneeId("");
            onChanged();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't add item");
        }
    }
    return (_jsxs("form", { className: "checklist-add-row", onSubmit: (e) => {
            e.preventDefault();
            submit();
        }, children: [_jsx("input", { className: "checklist-add-input", value: title, placeholder: "+ Add checklist item", onChange: (e) => setTitle(e.target.value) }), _jsxs("select", { className: "checklist-assignee", value: assigneeId, onChange: (e) => setAssigneeId(e.target.value), title: "Assign on add (optional)", children: [_jsx("option", { value: "", children: "\u2014 assign \u2014" }), members.map((m) => (_jsx("option", { value: m.id, children: m.name }, m.id)))] }), error && (_jsx("span", { className: "error checklist-add-error", role: "alert", children: error }))] }));
}
